import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Twitter, Linkedin, Github, Instagram, Globe, Camera, Trash2 } from "lucide-react";
import Shell from "../components/Shell";
import { Avatar } from "../components/bits";
import { useApp } from "../context/AppContext";
import { endpoints } from "../lib/api";

const SOCIALS = [
  { key: "x", label: "X (Twitter)", Icon: Twitter, ph: "@handle" },
  { key: "linkedin", label: "LinkedIn", Icon: Linkedin, ph: "in/your-name" },
  { key: "github", label: "GitHub", Icon: Github, ph: "username" },
  { key: "instagram", label: "Instagram", Icon: Instagram, ph: "@handle" },
  { key: "website", label: "Website", Icon: Globe, ph: "yoursite.com" },
];

export default function ProfilePage() {
  const { auth, refreshAuth } = useApp();
  const [p, setP] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const fileRef = React.useRef();

  React.useEffect(() => {
    if (auth.authenticated) endpoints.profileMe().then(setP).catch(() => {});
  }, [auth.authenticated]);

  if (auth.loading) return <Shell><div className="flex flex-1 items-center justify-center text-on-dark-muted">Loading…</div></Shell>;
  if (!auth.authenticated) {
    return <Shell><div className="flex flex-1 flex-col items-center justify-center gap-4 p-10 text-center">
      <h1 className="font-display text-[26px] font-bold text-white">Your profile</h1>
      <p className="text-on-dark-muted">Sign in to set up your photo, bio and social links.</p>
      <Link to="/signin" className="rounded-pill bg-gold-500 px-6 py-3 font-bold text-navy-900" data-testid="profile-signin-cta">Go to sign in</Link>
    </div></Shell>;
  }
  if (!p) return <Shell><div className="flex flex-1 items-center justify-center text-on-dark-muted">Loading…</div></Shell>;

  const setSocial = (k, v) => setP({ ...p, socials: { ...p.socials, [k]: v } });

  const save = async () => {
    setSaving(true);
    try {
      const updated = await endpoints.profileUpdate({ display_name: p.display_name, bio: p.bio, socials: p.socials });
      setP(updated);
      await refreshAuth();
      toast("Profile saved 💛");
    } catch { toast("Couldn't save. Try again."); } finally { setSaving(false); }
  };

  const onPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const r = await endpoints.profilePhoto(file);
      setP({ ...p, photo_url: r.photo_url });
      await refreshAuth();
      toast("Photo updated");
    } catch { toast("Upload failed"); } finally { setUploading(false); e.target.value = ""; }
  };
  const clearPhoto = async () => {
    await endpoints.profilePhotoClear();
    setP({ ...p, photo_url: null });
    await refreshAuth();
    toast("Photo removed");
  };

  const inp = "w-full rounded-md border border-white/15 bg-navy-800 px-3 py-2.5 text-[14px] text-white outline-none placeholder:text-ink-400 focus:border-gold-500";

  return (
    <Shell footer={false}>
      <div className="mx-auto w-full max-w-[560px] px-4 py-6" data-testid="profile-page">
        <h1 className="font-display text-[28px] font-extrabold text-white">Your profile</h1>
        <p className="mb-6 text-[13px] text-on-dark-muted">Signed in as {auth.email}. Your photo appears on the cards for projects you're on.</p>

        <div className="flex items-center gap-4">
          <Avatar name={p.display_name || auth.email} src={p.photo_url} size={88} className="ring-2 ring-white/10" />
          <div className="flex flex-col gap-2">
            <input ref={fileRef} type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid="profile-photo-input" />
            <button onClick={() => fileRef.current.click()} disabled={uploading} data-testid="profile-upload-photo" className="flex items-center gap-2 rounded-pill bg-gold-500 px-4 py-2 text-[13px] font-bold text-navy-900 disabled:opacity-50">
              <Camera size={15} /> {uploading ? "Uploading…" : p.photo_url ? "Change photo" : "Upload photo"}
            </button>
            {p.photo_url && <button onClick={clearPhoto} className="flex items-center gap-1.5 text-[13px] font-semibold text-coral-400"><Trash2 size={14} /> Remove</button>}
          </div>
        </div>

        <div className="mt-6 space-y-4">
          <label className="block">
            <span className="mb-1 block text-[13px] font-semibold text-white">Name</span>
            <input className={inp} value={p.display_name} maxLength={80} onChange={(e) => setP({ ...p, display_name: e.target.value })} placeholder="Your name" data-testid="profile-name" />
          </label>
          <label className="block">
            <span className="mb-1 block text-[13px] font-semibold text-white">Short bio</span>
            <textarea rows={3} className={inp} value={p.bio} maxLength={280} onChange={(e) => setP({ ...p, bio: e.target.value })} placeholder="A sentence or two about you" data-testid="profile-bio" />
            <span className="mt-1 block text-right text-[11px] text-ink-400">{(p.bio || "").length}/280</span>
          </label>

          <div>
            <span className="mb-2 block text-[13px] font-semibold text-white">Social handles</span>
            <div className="space-y-2.5">
              {SOCIALS.map(({ key, label, Icon, ph }) => (
                <div key={key} className="flex items-center gap-2.5">
                  <Icon size={18} className="shrink-0 text-on-dark-muted" />
                  <input className={inp} value={p.socials?.[key] || ""} onChange={(e) => setSocial(key, e.target.value)} placeholder={`${label} — ${ph}`} data-testid={`profile-social-${key}`} />
                </div>
              ))}
            </div>
          </div>

          <button onClick={save} disabled={saving} data-testid="profile-save" className="w-full rounded-pill bg-gold-500 py-3 font-display text-[16px] font-bold text-navy-900 disabled:opacity-50">
            {saving ? "Saving…" : "Save profile"}
          </button>
        </div>
      </div>
    </Shell>
  );
}
